import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { CourierHandoverMode, ScanListItem } from '../types';

interface DeletedScanPiece {
    pcsId: string;
    waybillNo: string;
    totPcs?: number;
}

interface CourierHandoverState {
    mode: CourierHandoverMode;
    scanList: ScanListItem[];
    deletedPieces: DeletedScanPiece[];
}

const initialState: CourierHandoverState = {
    mode: 'scan-in',
    scanList: [],
    deletedPieces: [],
};

const courierHandoverSlice = createSlice({
    name: 'courierHandover',
    initialState,
    reducers: {
        setMode(state, action: PayloadAction<CourierHandoverMode>) {
            state.mode = action.payload;
        },
        addScanItem(state, action: PayloadAction<ScanListItem>) {
            // Guard: do not add duplicate pcsId in the same session
            state.deletedPieces = state.deletedPieces.filter(
                (piece) => piece.pcsId !== action.payload.pcsId,
            );
            const exists = state.scanList.some((item) => item.pcsId === action.payload.pcsId);
            if (!exists) {
                state.scanList.push(action.payload);
            }
        },
        removeScanItem(state, action: PayloadAction<string>) {
            const item = state.scanList.find((piece) => piece.pcsId === action.payload);
            if (item?.waybillNo) {
                state.deletedPieces = state.deletedPieces.filter(
                    (piece) => piece.pcsId !== item.pcsId,
                );
                state.deletedPieces.push({
                    pcsId: item.pcsId,
                    waybillNo: item.waybillNo,
                    totPcs: item.totPcs,
                });
            }
            state.scanList = state.scanList.filter((item) => item.pcsId !== action.payload);
        },
        /**
         * Remove every piece that belongs to a given waybill.
         * Used by ShipmentIncompleteModal "Remove All" for a specific waybill group.
         */
        removeWaybillPieces(state, action: PayloadAction<string>) {
            state.scanList = state.scanList.filter((item) => item.waybillNo !== action.payload);
            state.deletedPieces = state.deletedPieces.filter(
                (piece) => piece.waybillNo !== action.payload,
            );
        },
        confirmScanItem(state, action: PayloadAction<string>) {
            const item = state.scanList.find((item) => item.pcsId === action.payload);
            if (item) {
                item.status = 'confirmed';
            }
        },
        clearScanList(state) {
            state.scanList = [];
            state.deletedPieces = [];
        },
        updateScanItemStatus(
            state,
            action: PayloadAction<{
                pcsId: string;
                status: ScanListItem['status'];
                errorMsgKey?: string;
                isPendingSibling?: boolean;
            }>,
        ) {
            const item = state.scanList.find((i) => i.pcsId === action.payload.pcsId);
            if (item) {
                item.status = action.payload.status;
                if (action.payload.errorMsgKey !== undefined) {
                    item.errorMsgKey = action.payload.errorMsgKey;
                }
                if (action.payload.isPendingSibling !== undefined) {
                    item.isPendingSibling = action.payload.isPendingSibling;
                }
            }
        },
        /**
         * When a multi-piece waybill scan returns MULTIPLE_PIECES_FOUND:
         * - The first-scanned piece is already in the list as 'valid'.
         * - This action stamps totPcs onto that piece and adds all remaining
         *   siblings as 'pending' (Not Received) rows grouped under the waybill.
         */
        addSiblingPieces(
            state,
            action: PayloadAction<{
                waybillNo: string;
                scannedPcsId: string;
                siblings: { pcsId: string }[];
                totPcs: number;
            }>,
        ) {
            const { waybillNo, scannedPcsId, siblings, totPcs } = action.payload;
            const siblingIds = new Set(siblings.map((sibling) => sibling.pcsId));
            state.deletedPieces = state.deletedPieces.filter(
                (piece) => !siblingIds.has(piece.pcsId),
            );

            // Stamp totPcs + waybillNo on every piece in the waybill that is
            // already in the list (the scanned piece plus any previously-scanned siblings).
            for (const item of state.scanList) {
                if (siblings.some((s) => s.pcsId === item.pcsId)) {
                    item.totPcs = totPcs;
                    item.waybillNo = waybillNo;
                }
            }

            // Add each sibling that is not yet in the list as a pending (Not Received) row.
            for (const sibling of siblings) {
                if (sibling.pcsId === scannedPcsId) continue;
                const alreadyExists = state.scanList.some((i) => i.pcsId === sibling.pcsId);
                if (!alreadyExists) {
                    state.scanList.push({
                        pcsId: sibling.pcsId,
                        waybillNo,
                        scannedIdentifier: sibling.pcsId,
                        status: 'pending',
                        totPcs,
                        isPendingSibling: true,
                    });
                }
            }
        },
        /**
         * Transitions a 'pending' sibling to 'valid' when the agent scans it.
         * Called optimistically before the validate API call returns.
         */
        confirmSiblingItem(state, action: PayloadAction<string>) {
            const item = state.scanList.find((i) => i.pcsId === action.payload);
            if (item && item.isPendingSibling) {
                item.isPendingSibling = false;
                item.status = 'valid';
            }
        },
    },
});

export const {
    setMode,
    addScanItem,
    removeScanItem,
    removeWaybillPieces,
    confirmScanItem,
    clearScanList,
    updateScanItemStatus,
    addSiblingPieces,
    confirmSiblingItem,
} = courierHandoverSlice.actions;

export default courierHandoverSlice.reducer;
